import React from 'react';
import { ArrowPathIcon } from '@heroicons/react/24/outline';

interface FileReplaceConfirmDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  newFile: File;
  existingDocument: {
    id: string;
    name: string;
    uploadedAt?: string;
    size?: number;
    status: string;
  };
}

export default function FileReplaceConfirmDialog({
  isOpen,
  onClose,
  onConfirm,
  newFile,
  existingDocument
}: FileReplaceConfirmDialogProps) {
  if (!isOpen) return null;

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-screen items-center justify-center p-4">
        {/* Background overlay */}
        <div
          className="fixed inset-0 bg-black/50 backdrop-blur-sm transition-opacity"
          onClick={onClose}
        />

        {/* Dialog */}
        <div className="relative bg-white dark:bg-slate-800 rounded-2xl shadow-xl max-w-sm w-full p-6 text-center">
          <div className="w-12 h-12 rounded-full bg-amber-100 dark:bg-amber-900/30 flex items-center justify-center mx-auto mb-4">
            <ArrowPathIcon className="h-6 w-6 text-amber-600 dark:text-amber-400" />
          </div>

          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
            Replace file?
          </h3>

          <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
            <span className="font-medium text-gray-700 dark:text-gray-300">"{newFile.name}"</span>
            {' '}already exists. Replace with new version?
          </p>

          <div className="text-left text-xs text-gray-500 dark:text-gray-400 mb-6 space-y-2">
            <div className="flex justify-between">
              <span>Current:</span>
              <span>{existingDocument.size ? formatFileSize(existingDocument.size) : 'Unknown size'}</span>
            </div>
            <div className="flex justify-between">
              <span>New:</span>
              <span>{formatFileSize(newFile.size)}</span>
            </div>
          </div>

          <div className="flex gap-3">
            <button
              type="button"
              className="flex-1 px-4 py-2.5 rounded-full border border-gray-300 dark:border-slate-600 text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-slate-700 transition-colors"
              onClick={onClose}
            >
              Cancel
            </button>
            <button
              type="button"
              className="flex-1 px-4 py-2.5 rounded-full bg-amber-600 text-sm font-medium text-white hover:bg-amber-700 transition-colors"
              onClick={onConfirm}
            >
              Replace
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
